import React, { useMemo, useState } from 'react';
import { Plus, PencilSimple, Trash, CircleNotch, FunnelSimple } from '@phosphor-icons/react';
import {
    getObligationStats,
    getNextUnpaidPayment,
    getTotalUnpaidAmount,
    formatEndDate,
    fmtPLN,
} from './obligationUtils';
import MarkMonthPaidButton from './MarkMonthPaidButton';
import NadplataPanel from './NadplataPanel';
import CreditViewSwitch from './CreditViewSwitch';

const PERIOD_OPTIONS = [
    { value: 'all', label: 'Wszystkie okresy' },
    { value: '01-15', label: '01–15' },
    { value: '16-30', label: '16–30' },
];

export default function PrivateCreditsTab({
    privateCredits,
    loading,
    onAddCredit,
    onEditCredit,
    onDeleteCredit,
    onRefresh,
}) {
    const [employeeFilter, setEmployeeFilter] = useState('all');
    const [periodFilter, setPeriodFilter] = useState('all');
    const [view, setView] = useState('lista');

    // Unique employees from the list (for filter dropdown)
    const employeeOptions = useMemo(() => {
        const map = new Map();
        for (const c of privateCredits) {
            if (c.employee_id && !map.has(c.employee_id)) {
                map.set(c.employee_id, c.employee_name || c.employee_id);
            }
        }
        return Array.from(map.entries())
            .sort((a, b) => a[1].localeCompare(b[1], 'pl'));
    }, [privateCredits]);

    const filtered = useMemo(() => {
        return privateCredits.filter((c) => {
            if (employeeFilter !== 'all' && c.employee_id !== employeeFilter) return false;
            if (periodFilter !== 'all' && (c.salary_period || '01-15') !== periodFilter) return false;
            return true;
        });
    }, [privateCredits, employeeFilter, periodFilter]);

    // Summary across filtered rows
    const summary = useMemo(() => {
        let nextSum = 0;
        let totalUnpaid = 0;
        let unpaidCount = 0;
        let creditsWithUnpaid = 0;
        for (const c of filtered) {
            const next = getNextUnpaidPayment(c.payments);
            if (next) {
                nextSum += next.amount;
                creditsWithUnpaid += 1;
            }
            const tot = getTotalUnpaidAmount(c.payments);
            totalUnpaid += tot;
            unpaidCount += (Array.isArray(c.payments) ? c.payments.filter(p => !p?.paid).length : 0);
        }
        return { nextSum, totalUnpaid, unpaidCount, creditsWithUnpaid, count: filtered.length };
    }, [filtered]);

    return (
        <div>
            <CreditViewSwitch view={view} setView={setView} kind="private" />
            {view === 'nadplata' ? <NadplataPanel kind="private" credits={privateCredits} onDone={onRefresh} /> : (<>
            <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                <h2 className="font-heading text-xl font-bold">
                    Kredyty prywatne ({filtered.length}{filtered.length !== privateCredits.length ? ` / ${privateCredits.length}` : ''})
                </h2>
                <button
                    onClick={() => onAddCredit('private')}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-private-credit-btn"
                >
                    <Plus className="w-4 h-4" /> Dodaj kredyt
                </button>
            </div>

            {/* Mark all paid for a month */}
            <div className="mb-4">
                <MarkMonthPaidButton
                    scope="private"
                    scopeLabel="kredytach prywatnych"
                    onDone={onRefresh}
                    testid="mark-paid-private"
                />
            </div>

            {/* Filters */}
            <div className="bg-white border border-border rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    <FunnelSimple className="w-4 h-4" /> Filtry:
                </div>
                <div>
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Pracownik</label>
                    <select
                        value={employeeFilter}
                        onChange={(e) => setEmployeeFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        data-testid="private-credits-employee-filter"
                    >
                        <option value="all">Wszyscy pracownicy</option>
                        {employeeOptions.map(([id, name]) => (
                            <option key={id} value={id}>{name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-1">Okres rata</label>
                    <select
                        value={periodFilter}
                        onChange={(e) => setPeriodFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-sm text-sm"
                        data-testid="private-credits-period-filter"
                    >
                        {PERIOD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>
                {(employeeFilter !== 'all' || periodFilter !== 'all') && (
                    <button
                        onClick={() => { setEmployeeFilter('all'); setPeriodFilter('all'); }}
                        className="text-xs text-slate-600 hover:text-rose-600 underline ml-auto"
                        data-testid="private-credits-clear-filters"
                    >
                        Wyczyść filtry
                    </button>
                )}
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">
                    {privateCredits.length === 0
                        ? 'Brak kredytów. Dodaj pierwszy!'
                        : 'Brak kredytów spełniających wybrane filtry.'}
                </div>
            ) : (
                <div className="bg-white border border-border rounded-sm overflow-x-auto -mx-3 sm:mx-0">
                    <table className="w-full min-w-[900px]">
                        <thead className="bg-muted">
                            <tr>
                                <th className="text-left py-3 px-4 field-label">Nazwa</th>
                                <th className="text-left py-3 px-4 field-label">Pracownik</th>
                                <th className="text-center py-3 px-4 field-label">Okres</th>
                                <th className="text-right py-3 px-4 field-label">Raty (opł. / razem)</th>
                                <th className="text-right py-3 px-4 field-label">Pozostało</th>
                                <th className="text-right py-3 px-4 field-label">Kwota najbliższej raty</th>
                                <th className="text-right py-3 px-4 field-label">Data zakończenia</th>
                                <th className="text-right py-3 px-4 field-label">Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(credit => {
                                const { paid, total, remaining, endDate } = getObligationStats(credit.payments);
                                const next = getNextUnpaidPayment(credit.payments);
                                const period = credit.salary_period || '01-15';
                                return (
                                    <tr key={credit.id} className="table-row" data-testid={`private-credit-row-${credit.id}`}>
                                        <td className="py-3 px-4 font-medium">{credit.name}</td>
                                        <td className="py-3 px-4 text-muted-foreground">{credit.employee_name || 'Unknown'}</td>
                                        <td className="py-3 px-4 text-center">
                                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                                {period}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right tabular-nums">{paid} / {total}</td>
                                        <td className="py-3 px-4 text-right">
                                            <span
                                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${remaining === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                                                data-testid={`private-credit-remaining-${credit.id}`}
                                            >
                                                {remaining} {remaining === 1 ? 'rata' : 'rat'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right tabular-nums font-mono" data-testid={`private-credit-next-${credit.id}`}>
                                            {next ? (
                                                <div>
                                                    <div className="font-semibold text-slate-800">{fmtPLN(next.amount)}</div>
                                                    {next.date && (
                                                        <div className="text-[10px] text-slate-500">
                                                            {next.date} · #{next.payment_number}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-emerald-600 text-xs font-semibold">— spłacone</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right text-sm text-slate-700 tabular-nums" data-testid={`private-credit-enddate-${credit.id}`}>
                                            {formatEndDate(endDate)}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button onClick={() => onEditCredit(credit, 'private')} className="p-2 hover:bg-muted rounded-sm">
                                                <PencilSimple className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => onDeleteCredit(credit.id, 'private')} className="p-2 hover:bg-red-50 text-red-600 rounded-sm">
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                            <tr>
                                <td colSpan={5} className="py-3 px-4 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider">
                                    Podsumowanie ({summary.count} {summary.count === 1 ? 'kredyt' : 'kredytów'}):
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <div className="text-xs text-slate-600">Najbliższe raty razem:</div>
                                    <div className="text-base font-bold tabular-nums font-mono text-emerald-700" data-testid="private-credits-next-sum">
                                        {fmtPLN(summary.nextSum)}
                                    </div>
                                </td>
                                <td colSpan={2} className="py-3 px-4 text-right">
                                    <div className="text-xs text-slate-600">Wszystkie nieopłacone razem ({summary.unpaidCount}):</div>
                                    <div className="text-base font-bold tabular-nums font-mono text-slate-800" data-testid="private-credits-total-unpaid">
                                        {fmtPLN(summary.totalUnpaid)}
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
            </>)}
        </div>
    );
}
